import { ChevronDown } from 'lucide-react';
import { forwardRef, type SelectHTMLAttributes } from 'react';

import { cn } from '../../lib/cn';
import {
  CONTROL_CLASSES,
  CONTROL_INVALID_CLASSES,
  Field,
  type FieldOwnProps,
} from '../Field/Field';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — a native `<select>`, styled.
 *
 * Native rather than a custom listbox on purpose. The mockups show a plain
 * dropdown (the contact form's "თემა" field), and a native select gets mobile
 * pickers, type-ahead and full screen-reader support for free — all of which a
 * hand-rolled combobox has to reimplement and usually gets wrong.
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends
    FieldOwnProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'required' | 'children'> {
  options: SelectOption[];
  /**
   * Shown as a disabled first option when nothing is selected. User-visible,
   * so it comes from the i18n bundle.
   */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hideLabel, hint, error, required, id, className, options, placeholder, ...props },
  ref,
) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      id={id}
      className={className}
    >
      {(field) => (
        <div className="relative">
          <select
            ref={ref}
            className={cn(
              CONTROL_CLASSES,
              'h-12 appearance-none pe-11',
              error && CONTROL_INVALID_CLASSES,
            )}
            {...field}
            {...props}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            aria-hidden="true"
            size={18}
            className="pointer-events-none absolute inset-y-0 end-4 my-auto text-secondary"
          />
        </div>
      )}
    </Field>
  );
});
